import React, { useMemo } from 'react';
import { useResponsiveLayout } from '@/hooks/ui/useResponsiveLayout';
import { deriveSecondary } from '@/pages/agent-interfaces/live-agent/components/GenieChatInputWeb';
import { ctaBorderRadius, resolveCtaColors } from '@/lib/ctaButtonStyle';
import { Loader2, Play, Square } from 'lucide-react';
import type { VoiceAgentControlsProps } from '@/types/voice/voice-agent-controls';

const DEFAULT_BRAND = "#4E9CFF";
const DEFAULT_SECONDARY = "#FF4DB8";

const VoiceAgentControls = ({
  connected,
  isConnecting = false,
  onToggle,
  brandColor,
  buttonText = {
    start: "Start Conversation",
    end: "End Conversation"
  },
  className = '',
  disabled = false,
  compact = false,
  variant = 'pill',
  secondaryColor,
  gradient = false,
  shape,
  colorOverride,
  colorOverride2,
  textColorOverride,
  sizeScale = 1,
}: VoiceAgentControlsProps) => {
  const { isMobile } = useResponsiveLayout();

  /* Match GenieChatInputWeb's smart fallback: HG brand blue snaps to HG pink,
     custom brands derive an analogous accent. Keeps the gradient on-brand for
     Help Genie's own widget instead of landing on the off-brand violet. */
  const gradientEnd = useMemo(() => {
    if (secondaryColor) return secondaryColor;
    if (brandColor === DEFAULT_BRAND) return DEFAULT_SECONDARY;
    return deriveSecondary(brandColor);
  }, [brandColor, secondaryColor]);

  const { start: ctaStart, end: ctaEnd, background: startBackground, color: startColor } = resolveCtaColors({
    brandColor,
    gradientEnd,
    connected,
    gradient,
    colorOverride,
    colorOverride2,
    textColorOverride,
  });

  const baseFontRem = compact ? 0.8 : isMobile ? 0.9 : 0.95;
  const basePaddingY = compact ? 5 : isMobile ? 10 : 8;
  const basePaddingX = compact ? 14 : isMobile ? 20 : 24;
  const baseMinWidth = compact ? undefined : isMobile ? 140 : 160;

  const buttonStyle: React.CSSProperties = {
    background: startBackground,
    color: startColor,
    border: gradient && !connected ? "none" : `2px solid ${ctaStart}`,
    borderRadius: ctaBorderRadius(shape),
    fontSize: `${baseFontRem * sizeScale}rem`,
    padding: `${basePaddingY * sizeScale}px ${basePaddingX * sizeScale}px`,
    boxShadow: gradient && !connected
      ? `0 10px 28px ${ctaStart}55, 0 4px 12px ${ctaEnd}40`
      : `0 4px 12px ${ctaStart}20`,
    cursor: disabled || isConnecting ? "default" : "pointer",
    fontWeight: 600,
    outline: "none",
    transition: "all 0.2s ease",
    minWidth: baseMinWidth ? baseMinWidth * sizeScale : undefined,
    userSelect: "none" as const,
    transform: "translateY(0)",
    letterSpacing: "0.5px",
    opacity: connected ? 0.9 : 1,
  };

  const handlePointerDown = (e: React.PointerEvent<HTMLButtonElement>) => {
    if (disabled || isConnecting) return;
    e.currentTarget.style.transform = "translateY(-2px)";
    e.currentTarget.style.boxShadow = gradient && !connected
      ? `0 14px 34px ${ctaStart}70, 0 6px 16px ${ctaEnd}50`
      : `0 6px 16px ${ctaStart}30`;
    if (connected) {
      e.currentTarget.style.opacity = "1";
    }
  };

  const handlePointerUp = (e: React.PointerEvent<HTMLButtonElement>) => {
    if (disabled || isConnecting) return;
    e.currentTarget.style.transform = "translateY(0)";
    e.currentTarget.style.boxShadow = gradient && !connected
      ? `0 10px 28px ${ctaStart}55, 0 4px 12px ${ctaEnd}40`
      : `0 4px 12px ${ctaStart}20`;
    if (connected) {
      e.currentTarget.style.opacity = "0.9";
    }
  };

  if (variant === 'orb') {
    const label = isConnecting ? 'Connecting to the Genie' : connected ? 'End conversation' : 'Start voice conversation';
    return (
      <div className={className} style={{ position: 'relative', zIndex: 10 }}>
        <button
          type="button"
          onClick={onToggle}
          disabled={disabled || isConnecting}
          aria-label={label}
          aria-busy={isConnecting}
          title={label}
          className="flex h-14 w-20 items-center justify-center rounded-full border-0 transition-opacity hover:opacity-75 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-genie-primary focus-visible:ring-offset-4 disabled:cursor-wait disabled:opacity-50"
          style={{ background: `linear-gradient(135deg, ${brandColor}20, ${gradientEnd}14)`, color: brandColor }}
        >
          {isConnecting ? (
            <Loader2 size={22} aria-hidden="true" className="animate-spin motion-reduce:animate-none" />
          ) : connected ? (
            <Square size={18} fill="currentColor" aria-hidden="true" />
          ) : (
            <Play size={22} fill="currentColor" strokeWidth={1.5} aria-hidden="true" className="ml-0.5" />
          )}
        </button>
      </div>
    );
  }

  return (
    <div className={`${className}`} style={{ zIndex: 10 }}>
      <button
        onClick={onToggle}
        style={buttonStyle}
        onPointerDown={handlePointerDown}
        onPointerUp={handlePointerUp}
        onPointerLeave={handlePointerUp}
        disabled={disabled || isConnecting}
      >
        {connected ? buttonText.end : buttonText.start}
      </button>
    </div>
  );
};

export default VoiceAgentControls;
